import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  Image,
  Button,
  Stack,
} from "@chakra-ui/react";
import CloseIcon from "../../../assets/images/employee/closeIcon.svg";
import {
  InputControl,
  InputDatePicker,
  SelectControl,
} from "../../../components/form/formControls";
import { useForm } from "react-hook-form";
import { useCategories } from "../../../categorys/jobTitle/detail";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

const schema = Yup.object().shape({
  employeeId: Yup.string().required("Vui lòng chọn nhân viên"),

  month: Yup.number()
    .required("Vui lòng chọn tháng năm")
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Tháng phải là số")
    .min(1, "Tháng phải từ 1 đến 12")
    .max(12, "Tháng phải từ 1 đến 12"),

  year: Yup.number()
    .required("Vui lòng chọn tháng năm")
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Năm phải là số")
    .min(2000, "Năm không hợp lệ")
    .max(2100, "Năm không hợp lệ"),

  department: Yup.string().required("Vui lòng chọn loại nhân viên"),

  commissionRate: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }

      if (typeof originalValue === "string") {
        const trimmed = originalValue.trim();
        if (trimmed === "") return null;
        return Number(trimmed);
      }

      return value;
    })
    .typeError("Vui lòng nhập số")
    .min(0, "Tỉ lệ phải lớn hơn hoặc bằng 0")
    .max(100, "Tỉ lệ phải nhỏ hơn hoặc bằng 100"),

  jobPositionId: Yup.string().required("Vui lòng chọn chức vụ"),
  businessCategoryId: Yup.string().required("Vui lòng chọn phân loại"),

  workingDays: Yup.number()
    .required("Vui lòng nhập số")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Vui lòng nhập số")
    .min(0, "Tổng số công phải lớn hơn hoặc bằng 0")
    .max(100, "Tổng số công phải nhỏ hơn hoặc bằng 100"),

  totalStandardWorking: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Vui lòng nhập số công chuẩn")
    .min(0, "Tổng số công chuẩn phải lớn hơn hoặc bằng 0")
    .max(100, "Tổng số công chuẩn phải nhỏ hơn hoặc bằng 100"),

  supportDepartmentId: Yup.string().nullable(),
  supportDepartmentManagerId: Yup.string().nullable(),

  saleAreaId: Yup.string().nullable(),
  saleBlockId: Yup.string().nullable(),
  saleAreaManagerId: Yup.string().nullable(),
  saleBlockManagerId: Yup.string().nullable(),

  branchId: Yup.string().nullable(),
  branchManagerId: Yup.string().nullable(),
});

const DEPARTMENT_SUPPORT = "0";
const DEPARTMENT_BUSINESS = "1";

const FormJobTitleCreate = ({ isOpen, onClose, defaultValues, onSubmit }) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const department = String(watch("department") ?? "");
  const saleAreaId = watch("saleAreaId");

  const { constantData } = useCategories(saleAreaId);

  const [monthYearDate, setMonthYearDate] = useState(null);
  const [zIndex, setZIndex] = useState("");
  const [focusStates, setFocusStates] = useState({});

  const formatEntriesForSelect = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({
      value: key,
      label:
        typeof value === "object"
          ? value?.fullName || value?.name || ""
          : value,
      code: typeof value === "object" ? value?.code || "" : "",
    }));

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);

      if (defaultValues?.month && defaultValues?.year) {
        setMonthYearDate(
          new Date(
            Number(defaultValues.year),
            Number(defaultValues.month) - 1,
            1,
          ),
        );
      } else {
        setMonthYearDate(null);
      }
    }
  }, [isOpen, defaultValues, reset]);

  const jobPositionSupportBussines = useMemo(
    () => formatEntriesForSelect(constantData?.jobPositionSupportBussines),
    [constantData?.jobPositionSupportBussines],
  );

  const jobPositionBussines = useMemo(
    () => formatEntriesForSelect(constantData?.jobPositionBussines),
    [constantData?.jobPositionBussines],
  );

  const businessCategorys = useMemo(
    () => formatEntriesForSelect(constantData?.businessCategorys),
    [constantData?.businessCategorys],
  );

  const saleAreas = useMemo(
    () => formatEntriesForSelect(constantData?.saleAreas),
    [constantData?.saleAreas],
  );

  const saleBlocks = useMemo(
    () => formatEntriesForSelect(constantData?.saleBlocks),
    [constantData?.saleBlocks],
  );

  const supportDepartments = useMemo(
    () => formatEntriesForSelect(constantData?.supportDepartments),
    [constantData?.supportDepartments],
  );

  const branches = useMemo(
    () => formatEntriesForSelect(constantData?.branches),
    [constantData?.branches],
  );

  const departments = useMemo(
    () => formatEntriesForSelect(constantData?.departmentByNumbers),
    [constantData?.departmentByNumbers],
  );

  const employeeSalesPerson = useMemo(
    () => formatEntriesForSelect(constantData?.employeeSalesPerson),
    [constantData?.employeeSalesPerson],
  );

  const employeeBusinessSupportStaff = useMemo(
    () => formatEntriesForSelect(constantData?.employeeBusinessSupportStaff),
    [constantData?.employeeBusinessSupportStaff],
  );

  const employeeBranchManagers = useMemo(() => {
    return formatEntriesForSelect(
      constantData?.employeeBranchManagers || constantData?.employeeSalesPerson,
    );
  }, [constantData?.employeeBranchManagers, constantData?.employeeSalesPerson]);

  const employeeOptions = useMemo(() => {
    const merged = [
      ...(employeeBusinessSupportStaff || []),
      ...(employeeSalesPerson || []),
      ...(employeeBranchManagers || []),
    ];

    const mapByValue = new Map();
    merged.forEach((item) => {
      if (!mapByValue.has(item.value)) {
        mapByValue.set(item.value, item);
      }
    });

    return Array.from(mapByValue.values());
  }, [
    employeeBusinessSupportStaff,
    employeeSalesPerson,
    employeeBranchManagers,
  ]);

  const jobPositionOptions = useMemo(() => {
    if (department === DEPARTMENT_SUPPORT) return jobPositionSupportBussines;
    if (department === DEPARTMENT_BUSINESS) return jobPositionBussines;
    return [];
  }, [department, jobPositionSupportBussines, jobPositionBussines]);

  useEffect(() => {
    setValue("jobPositionId", "", { shouldValidate: false });

    if (department === DEPARTMENT_SUPPORT) {
      setValue("saleAreaId", "", { shouldValidate: false });
      setValue("saleBlockId", "", { shouldValidate: false });
      setValue("saleAreaManagerId", "", { shouldValidate: false });
      setValue("saleBlockManagerId", "", { shouldValidate: false });
    }

    if (department === DEPARTMENT_BUSINESS) {
      setValue("supportDepartmentId", "", { shouldValidate: false });
      setValue("supportDepartmentManagerId", "", { shouldValidate: false });
    }

    if (!department) {
      setValue("supportDepartmentId", "", { shouldValidate: false });
      setValue("supportDepartmentManagerId", "", { shouldValidate: false });
      setValue("saleAreaId", "", { shouldValidate: false });
      setValue("saleBlockId", "", { shouldValidate: false });
      setValue("saleAreaManagerId", "", { shouldValidate: false });
      setValue("saleBlockManagerId", "", { shouldValidate: false });
    }
  }, [department, setValue]);

  const handleFocus = (field) => {
    setZIndex(field);
    setFocusStates((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setZIndex("");
    setFocusStates((prev) => ({ ...prev, [field]: false }));
  };

  const onChangeField = (field, option) => {
    setValue(field, option, { shouldValidate: true });
  };

  const handleMonthYearChange = (selectedDate) => {
    setMonthYearDate(selectedDate);

    if (!selectedDate) {
      setValue("month", "", { shouldValidate: true });
      setValue("year", "", { shouldValidate: true });
      return;
    }

    setValue("month", selectedDate.getMonth() + 1, { shouldValidate: true });
    setValue("year", selectedDate.getFullYear(), { shouldValidate: true });
  };

  const submitHandler = (data) => {
    onSubmit(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        h={"86.5vh"}
        maxH={"625px"}
        w={"100%"}
        maxW={"625px"}
        overflowY={"scroll"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
          "&::-moz-scrollbar": {
            width: "4px",
          },
          "&::-moz-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
        }}
      >
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex flexDirection={"column"} h={"100%"}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
              padding={"25px 24px"}
              borderBottom={"1px solid var(--bg-line-employee)"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                Thêm xác nhận công và chức danh
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <Image src={CloseIcon} />
              </Box>
            </Flex>

            <Flex flex={"1"} p={"24px"} flexDirection={"column"} h={"100%"}>
              <Box
                flex={1}
                w={"100%"}
                maxW={"545px"}
                minH={"375px"}
                m={"24px auto"}
              >
                <Flex gap={"27px"} mb={"12px"}>
                  <SelectControl
                    label="Nhân viên"
                    dataOption={employeeOptions}
                    selectedMain={watch("employeeId")}
                    placeholder="Chọn nhân viên"
                    isSearchable={true}
                    onChange={(option) => onChangeField("employeeId", option)}
                    onFocus={() => handleFocus("employeeId")}
                    onBlur={() => handleBlur("employeeId")}
                    state={focusStates["employeeId"]}
                    errorMessage={errors.employeeId}
                    isRequired={true}
                  />
                  <SelectControl
                    label="Loại nhân viên"
                    dataOption={departments}
                    selectedMain={watch("department")}
                    placeholder="Chọn loại nhân viên"
                    isSearchable={false}
                    onChange={(option) => onChangeField("department", option)}
                    onFocus={() => handleFocus("department")}
                    onBlur={() => handleBlur("department")}
                    state={focusStates["department"]}
                    errorMessage={errors.department}
                    isRequired={true}
                  />
                </Flex>

                <Flex gap={"27px"} mb={"12px"} alignItems={"flex-start"}>
                  <Box flex={1}>
                    <InputDatePicker
                      label={"Tháng năm"}
                      focusStates={focusStates["monthYear"]}
                      selected={monthYearDate}
                      onFocus={() => handleFocus("monthYear")}
                      onBlur={() => handleBlur("monthYear")}
                      onChange={handleMonthYearChange}
                      dateFormat="MM/yyyy"
                      locale="vi"
                      placeholderText="Chọn tháng năm"
                      showMonthYearPicker
                      errors={errors?.month || errors?.year}
                      formControlStyle={{
                        maxW: "100%",
                      }}
                      formLabelStyle={{
                        fontWeight: 400,
                        lineHeight: "15px",
                        fontSize: "12px",
                        mb: "6px",
                      }}
                      zIndex={zIndex === "monthYear" ? 3 : 0}
                      isRequired={true}
                    />
                  </Box>
                  <Box flex={1}>
                    <InputControl
                      label="Tổng số công"
                      placeholder="Nhập tổng số công"
                      errors={errors.workingDays}
                      register={{
                        name: "workingDays",
                        value: watch("workingDays"),
                        onChange: (e) =>
                          setValue("workingDays", e.target.value, {
                            shouldValidate: true,
                          }),
                      }}
                      onFocus={() => handleFocus("workingDays")}
                      onBlur={() => handleBlur("workingDays")}
                      state={focusStates["workingDays"]}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Box>
                </Flex>

                <Flex gap={"27px"} mb={"12px"}>
                  <InputControl
                    label="Tổng số công chuẩn"
                    placeholder="Nhập tổng số công chuẩn"
                    errors={errors.totalStandardWorking}
                    register={{
                      name: "totalStandardWorking",
                      value: watch("totalStandardWorking"),
                      onChange: (e) =>
                        setValue("totalStandardWorking", e.target.value, {
                          shouldValidate: true,
                        }),
                    }}
                    onFocus={() => handleFocus("totalStandardWorking")}
                    onBlur={() => handleBlur("totalStandardWorking")}
                    state={focusStates["totalStandardWorking"]}
                    mwidth={"100%"}
                  />
                  <InputControl
                    label="Tỷ lệ phí dịch vụ"
                    placeholder="Nhập tỷ lệ phí dịch vụ"
                    errors={errors.commissionRate}
                    register={{
                      name: "commissionRate",
                      value: watch("commissionRate"),
                      onChange: (e) =>
                        setValue("commissionRate", e.target.value, {
                          shouldValidate: true,
                        }),
                    }}
                    onFocus={() => handleFocus("commissionRate")}
                    onBlur={() => handleBlur("commissionRate")}
                    state={focusStates["commissionRate"]}
                    mwidth={"100%"}
                  />
                </Flex>

                <Flex gap={"27px"} mb={"12px"}>
                  <SelectControl
                    label="Phân loại"
                    dataOption={businessCategorys}
                    selectedMain={watch("businessCategoryId")}
                    placeholder="Chọn phân loại"
                    isSearchable={true}
                    onChange={(option) =>
                      onChangeField("businessCategoryId", option)
                    }
                    onFocus={() => handleFocus("businessCategoryId")}
                    onBlur={() => handleBlur("businessCategoryId")}
                    state={focusStates["businessCategoryId"]}
                    errorMessage={errors.businessCategoryId}
                    isRequired={true}
                  />
                  <SelectControl
                    label="Chức vụ"
                    dataOption={jobPositionOptions}
                    selectedMain={watch("jobPositionId")}
                    placeholder={
                      department ? "Chọn chức vụ" : "Chọn loại nhân viên trước"
                    }
                    isSearchable={true}
                    onChange={(option) =>
                      onChangeField("jobPositionId", option)
                    }
                    onFocus={() => handleFocus("jobPositionId")}
                    onBlur={() => handleBlur("jobPositionId")}
                    state={focusStates["jobPositionId"]}
                    errorMessage={errors.jobPositionId}
                    isRequired={true}
                  />
                </Flex>

                <Flex gap={"27px"} mb={"12px"}>
                  <SelectControl
                    label="Chi nhánh"
                    dataOption={branches}
                    selectedMain={watch("branchId")}
                    placeholder="Chọn chi nhánh"
                    isSearchable={true}
                    onChange={(option) => onChangeField("branchId", option)}
                    onFocus={() => handleFocus("branchId")}
                    onBlur={() => handleBlur("branchId")}
                    state={focusStates["branchId"]}
                    errorMessage={errors.branchId}
                  />
                  <SelectControl
                    label="Giám đốc chi nhánh"
                    dataOption={employeeBranchManagers}
                    selectedMain={watch("branchManagerId")}
                    placeholder="Chọn giám đốc chi nhánh"
                    isSearchable={true}
                    onChange={(option) =>
                      onChangeField("branchManagerId", option)
                    }
                    onFocus={() => handleFocus("branchManagerId")}
                    onBlur={() => handleBlur("branchManagerId")}
                    state={focusStates["branchManagerId"]}
                    errorMessage={errors.branchManagerId}
                  />
                </Flex>

                {department === DEPARTMENT_SUPPORT && (
                  <Flex gap={"27px"} mb={"12px"}>
                    <SelectControl
                      label="Phòng ban"
                      dataOption={supportDepartments}
                      selectedMain={watch("supportDepartmentId")}
                      placeholder="Chọn phòng ban"
                      isSearchable={true}
                      onChange={(option) =>
                        onChangeField("supportDepartmentId", option)
                      }
                      onFocus={() => handleFocus("supportDepartmentId")}
                      onBlur={() => handleBlur("supportDepartmentId")}
                      state={focusStates["supportDepartmentId"]}
                      errorMessage={errors.supportDepartmentId}
                    />
                    <SelectControl
                      label="Trưởng phòng"
                      dataOption={employeeBusinessSupportStaff}
                      selectedMain={watch("supportDepartmentManagerId")}
                      placeholder="Chọn trưởng phòng"
                      isSearchable={true}
                      onChange={(option) =>
                        onChangeField("supportDepartmentManagerId", option)
                      }
                      onFocus={() => handleFocus("supportDepartmentManagerId")}
                      onBlur={() => handleBlur("supportDepartmentManagerId")}
                      state={focusStates["supportDepartmentManagerId"]}
                      errorMessage={errors.supportDepartmentManagerId}
                    />
                  </Flex>
                )}

                {department === DEPARTMENT_BUSINESS && (
                  <>
                    <Flex gap={"27px"} mb={"12px"}>
                      <SelectControl
                        label="Vùng"
                        dataOption={saleAreas}
                        selectedMain={watch("saleAreaId")}
                        placeholder="Chọn vùng"
                        isSearchable={true}
                        onChange={(option) =>
                          onChangeField("saleAreaId", option)
                        }
                        onFocus={() => handleFocus("saleAreaId")}
                        onBlur={() => handleBlur("saleAreaId")}
                        state={focusStates["saleAreaId"]}
                        errorMessage={errors.saleAreaId}
                      />
                      <SelectControl
                        label="Khối"
                        selectedMain={watch("saleBlockId")}
                        dataOption={saleBlocks}
                        isSearchable={true}
                        placeholder="Chọn khối"
                        errorMessage={errors.saleBlockId}
                        onChange={(option) =>
                          onChangeField("saleBlockId", option)
                        }
                        onFocus={() => handleFocus("saleBlockId")}
                        onBlur={() => handleBlur("saleBlockId")}
                        state={focusStates["saleBlockId"]}
                      />
                    </Flex>

                    <Flex gap={"27px"} mb={"12px"}>
                      <SelectControl
                        label="Trưởng vùng"
                        dataOption={employeeSalesPerson}
                        selectedMain={watch("saleAreaManagerId")}
                        placeholder="Chọn trưởng vùng"
                        isSearchable={true}
                        onChange={(option) =>
                          onChangeField("saleAreaManagerId", option)
                        }
                        onFocus={() => handleFocus("saleAreaManagerId")}
                        onBlur={() => handleBlur("saleAreaManagerId")}
                        state={focusStates["saleAreaManagerId"]}
                        errorMessage={errors.saleAreaManagerId}
                      />
                      <SelectControl
                        label="Trưởng khối"
                        selectedMain={watch("saleBlockManagerId")}
                        dataOption={employeeSalesPerson}
                        isSearchable={true}
                        placeholder="Chọn trưởng khối"
                        onChange={(option) =>
                          onChangeField("saleBlockManagerId", option)
                        }
                        onFocus={() => handleFocus("saleBlockManagerId")}
                        onBlur={() => handleBlur("saleBlockManagerId")}
                        state={focusStates["saleBlockManagerId"]}
                        errorMessage={errors.saleBlockManagerId}
                      />
                    </Flex>
                  </>
                )}
              </Box>

              <Stack
                direction="row"
                spacing={4}
                align="center"
                justify={"flex-end"}
              >
                <Button
                  fontSize={"15px"}
                  fontWeight={"600"}
                  bg="var(--color-main)"
                  w={"112px"}
                  h={"44px"}
                  border={"1px solid var(--color-option-employee-hover)"}
                  color={"var(--color-option-employee-hover)"}
                  _hover={{ filter: "brightness(90%)" }}
                  onClick={onClose}
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  fontSize={"15px"}
                  fontWeight={"600"}
                  color={"var(--color-main)"}
                  bg="var(--linear-gradient-employee)"
                  w={"112px"}
                  h={"44px"}
                  _hover={{ filter: "brightness(90%)" }}
                >
                  Thêm mới
                </Button>
              </Stack>
            </Flex>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default FormJobTitleCreate;
